import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import { Home, RefrigeratorIcon, Book, PlusCircle, User, Camera, Plus, Sparkles, X, UtensilsCrossed, Image, FolderOpen } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { optimizeImage } from '../../lib/api';
import { MealGenerationModal, MealGenerationOptions } from '../ui/meal-generation-modal';
import { IngredientSourceModal } from '../ui/ingredient-source-modal';
import { useNoshStore } from '../../lib/store';

const MobileNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [currentScanType, setCurrentScanType] = useState<'inventory' | 'meal'>('inventory');
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [clickedButton, setClickedButton] = useState<string | null>(null);
  
  // Get store functions for ingredient source modal
  const { 
    inventory,
    user,
    ingredientSource,
    showIngredientSourceModal,
    hideIngredientSourceModal,
    setSelectedIngredientSource,
    updateFloatingBarTheme
  } = useNoshStore();

  // Debug theme changes
  useEffect(() => {
    console.log('🎨 MobileNav Theme Debug:', {
      userTheme: user.floatingBarTheme,
      appliedClass: user.floatingBarTheme === 'dark' ? 'floating-bar-dark' : 'floating-bar-light'
    });
  }, [user.floatingBarTheme]);

  // Auto-close Quick Actions popup when navigating to different pages
  useEffect(() => {
    if (showQuickActions) {
      setShowQuickActions(false);
      setShowScanModal(false);
    }
  }, [location.pathname]);
  
  // File input refs for different scan types and methods
  const inventoryCameraRef = useRef<HTMLInputElement>(null);
  const inventoryFileRef = useRef<HTMLInputElement>(null);
  const mealCameraRef = useRef<HTMLInputElement>(null);
  const mealFileRef = useRef<HTMLInputElement>(null);

  const navItems = [
    { 
      icon: <Home size={24} />, 
      label: 'Home', 
      path: '/dashboard', 
      active: location.pathname === '/dashboard' 
    },
    { 
      icon: <RefrigeratorIcon size={24} />, 
      label: 'Inventory', 
      path: '/inventory',
      active: location.pathname === '/inventory'
    },
    { 
      icon: showQuickActions ? <X size={32} /> : <PlusCircle size={32} />, 
      label: 'Add', 
      path: '#',
      active: false,
      special: true
    },
    { 
      icon: <Book size={24} />, 
      label: 'Recipes', 
      path: '/recipes',
      active: location.pathname === '/recipes'
    },
    { 
      icon: <User size={24} />, 
      label: 'Profile', 
      path: '/profile',
      active: location.pathname === '/profile'
    }
  ];

  // Handle file selection and dispatch events
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, scanType: 'inventory' | 'meal') => {
    const file = e.target.files?.[0];
    if (file) {
      // Debug logging for mobile standalone mode
      const isStandalone = (window.navigator as any).standalone || 
                          window.matchMedia('(display-mode: standalone)').matches;
      const isMobile = window.innerWidth < 768;
      const isMobileStandalone = isMobile && isStandalone;
      
      // Detect image source based on input element
      const isCamera = e.target.hasAttribute('capture');
      const imageSource = isCamera ? 'camera' : 'library';
      
      if (isMobileStandalone) {
        console.log('🔍 [PWA Debug] MobileNav handleFileChange:', {
          scanType,
          imageSource,
          isCamera,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          isMobile,
          isStandalone,
          isMobileStandalone
        });
      }

      try {
        // Mobile-specific image optimization and validation
        console.log('📱 [Mobile] Original file:', {
          name: file.name,
          size: Math.round(file.size / 1024) + 'KB',
          type: file.type,
          source: imageSource
        });

        // Validate file type and convert HEIC to JPEG
        if (!file.type.startsWith('image/')) {
          console.error('❌ Invalid file type:', file.type);
          return;
        }

        // Check for HEIC format and warn user (browser should auto-convert but add logging)
        if (file.type === 'image/heic' || file.type === 'image/heif' || file.name.toLowerCase().includes('.heic')) {
          console.log('📱 [Mobile] HEIC file detected, will convert to JPEG');
        }

        // Apply source-aware optimization for mobile with AI-analysis for ingredient detection
        // Use AI-optimized compression when this is for ingredient detection
        const purpose = scanType === 'meal' ? 'ai-analysis' : 'general';
        const optimizedFile = await optimizeImage(file, 500, 0.7, imageSource, purpose);
        
        console.log('📱 [Mobile] Optimized file:', {
          name: optimizedFile.name,
          size: Math.round(optimizedFile.size / 1024) + 'KB',
          type: optimizedFile.type,
          source: imageSource,
          reduction: Math.round(((file.size - optimizedFile.size) / file.size) * 100) + '%'
        });

        if (scanType === 'meal') {
          const mealScanEvent = new CustomEvent('triggerMealScan', { detail: { file: optimizedFile } });
          window.dispatchEvent(mealScanEvent);
          if (isMobileStandalone) {
            console.log('🔍 [PWA Debug] Dispatched triggerMealScan event with optimized file');
          }
        } else {
          const inventoryScanEvent = new CustomEvent('triggerInventoryScan', { detail: { file: optimizedFile } });
          window.dispatchEvent(inventoryScanEvent);
          if (isMobileStandalone) {
            console.log('🔍 [PWA Debug] Dispatched triggerInventoryScan event with optimized file');
          }
        }
      } catch (error) {
        console.error('❌ [Mobile] Image optimization failed:', error);
        // Fallback to original file if optimization fails
        if (scanType === 'meal') {
          const mealScanEvent = new CustomEvent('triggerMealScan', { detail: { file } });
          window.dispatchEvent(mealScanEvent);
        } else {
          const inventoryScanEvent = new CustomEvent('triggerInventoryScan', { detail: { file } });
          window.dispatchEvent(inventoryScanEvent);
        }
      }
    }
    // Reset input value and close modals
    e.target.value = '';
    setShowScanModal(false);
    setShowQuickActions(false);
  };

  // Handle showing scan modal
  const handleShowScanModal = (scanType: 'inventory' | 'meal') => {
    setCurrentScanType(scanType);
    setShowQuickActions(false); // Close quick actions
    setShowScanModal(true); // Show scan options
  };

  // Scan options for the modal
  const scanOptions = [
    {
      icon: <Camera size={20} />,
      label: 'Take Photo',
      description: 'Use camera to capture',
      action: () => {
        setTimeout(() => {
          if (currentScanType === 'inventory' && inventoryCameraRef.current) {
            inventoryCameraRef.current.click();
          } else if (currentScanType === 'meal' && mealCameraRef.current) {
            mealCameraRef.current.click();
          }
        }, 100);
      }
    },
    {
      icon: <Image size={20} />,
      label: 'Photo Library',
      description: 'Choose from gallery',
      action: () => {
        setTimeout(() => {
          if (currentScanType === 'inventory' && inventoryFileRef.current) {
            inventoryFileRef.current.click();
          } else if (currentScanType === 'meal' && mealFileRef.current) {
            mealFileRef.current.click();
          }
        }, 100);
      }
    }
  ];

  const quickActions = [
    {
      icon: <Camera size={20} />,
      label: 'Scan Item',
      description: 'Take photo to add items',
      action: () => handleShowScanModal('inventory')
    },
    {
      icon: <UtensilsCrossed size={20} />,
      label: 'Log Meal from Photo',
      description: 'Analyze nutrition from meals',
      action: () => handleShowScanModal('meal')
    },
    {
      icon: <Sparkles size={20} />,
      label: 'Generate Recipe',
      description: 'AI recipe from inventory',
      action: () => {
        setShowQuickActions(false);
        showIngredientSourceModal();
      }
    }
  ];

  // Handle ingredient source selection
  const handleIngredientSourceSelected = (sourceType: 'inventory' | 'mixed' | 'photo', photoIngredients?: string[]) => {
    console.log('🎯 [MOBILE NAV] Ingredient source selected:', sourceType, photoIngredients);
    
    // Store the selected source and detected ingredients
    setSelectedIngredientSource(sourceType, photoIngredients);
    
    // Show the meal generation modal
    setShowRecipeModal(true);
  };

  // Handle back from meal generation to ingredient source
  const handleMealGenerationBack = () => {
    console.log('🔙 [MOBILE NAV] User wants to go back to ingredient source');
    
    // Close meal generation modal
    setShowRecipeModal(false);
    
    // Reopen ingredient source modal
    showIngredientSourceModal();
  };

  const handleRecipeGenerate = async (options: MealGenerationOptions) => {
    const { 
      generateRecipeWithAI, 
      generateRecipeFromPhoto,
      startRecipeGeneration,
      setRecipeGenerationError,
      showRecipeResult
    } = useNoshStore.getState();
    
    try {
      // Start the loading modal
      startRecipeGeneration();
      
      // Check if we have detected ingredients from photo
      const hasDetectedIngredients = ingredientSource.detectedIngredients && ingredientSource.detectedIngredients.length > 0;
      
      let result;
      if (hasDetectedIngredients) {
        // Call the photo-specific AI generation function
        result = await generateRecipeFromPhoto(ingredientSource.detectedIngredients, options);
      } else {
        // Call the regular AI generation function
        result = await generateRecipeWithAI(options);
      }
      
      if (result.success && result.recipe) {
        // Show the generated recipe with proper photo context
        // Include detected ingredients in generation options for photo recipes
        const enhancedOptions = hasDetectedIngredients 
          ? { ...options, detectedIngredients: ingredientSource.detectedIngredients, source: 'photo' }
          : options;
        
        showRecipeResult(result.recipe, enhancedOptions);
      } else {
        setRecipeGenerationError(result.error || 'Recipe generation failed');
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate recipe';
      setRecipeGenerationError(errorMessage);
    }
  };

  const handlePlusClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowQuickActions(!showQuickActions);
  };

  // Handle button click with bounce animation
  const handleButtonClick = (path: string, isSpecial: boolean, originalOnClick?: (e: React.MouseEvent) => void) => {
    return (e: React.MouseEvent) => {
      if (isSpecial && originalOnClick) {
        originalOnClick(e);
        return;
      }
      
      // Trigger bounce animation
      setClickedButton(path);
      
      // Reset animation after a short delay
      setTimeout(() => {
        setClickedButton(null);
      }, 400);
    };
  };

  return (
    <motion.div 
      className="mobile-navigation fixed bottom-0 left-0 right-0 p-4 z-50"
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      {/* Ingredient Source Selection Modal */}
      <IngredientSourceModal
        isOpen={ingredientSource.showModal}
        onClose={hideIngredientSourceModal}
        onSourceSelected={handleIngredientSourceSelected}
        inventoryCount={inventory.length}
        isMobile={true}
      />

      {/* Meal Generation Modal */}
      <MealGenerationModal
        isOpen={showRecipeModal}
        onClose={() => setShowRecipeModal(false)}
        onBack={handleMealGenerationBack}
        onGenerate={handleRecipeGenerate}
        inventoryCount={inventory.length}
        shoppingItemsCount={5} // TODO: Get actual shopping items count from store
        detectedIngredients={ingredientSource.detectedIngredients}
      />

      {/* Scan Options Modal */}
      <AnimatePresence>
        {showScanModal && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            className="absolute bottom-20 left-4 right-4 z-20"
          >
            <div className="bg-card rounded-3xl shadow-2xl border border-border overflow-hidden mx-auto max-w-md">
              <div className="p-6 space-y-4">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    {currentScanType === 'inventory' ? 'Scan Item' : 'Scan Meal'}
                  </h3>
                  <p className="text-sm text-muted-foreground">Choose how you'd like to capture</p>
                </div>
                
                <div className="space-y-3">
                  {scanOptions.map((option, index) => (
                    <motion.button
                      key={option.label}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={option.action}
                      className="w-full flex items-center p-4 rounded-2xl bg-muted hover:bg-muted/80 transition-colors group"
                    >
                      <div className="bg-primary text-white p-3 rounded-xl mr-4 group-hover:bg-primary-hover transition-colors">
                        {option.icon}
                      </div>
                      <div className="flex-1 text-left">
                        <h4 className="font-medium text-foreground">{option.label}</h4>
                        <p className="text-sm text-muted-foreground">{option.description}</p>
                      </div>
                    </motion.button>
                  ))}
                </div>
                
                <button
                  onClick={() => setShowScanModal(false)}
                  className="w-full py-3 text-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Actions Popup */}
      <AnimatePresence>
        {showQuickActions && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            className="absolute bottom-24 left-4 right-4 z-10"
          >
            <div className="bg-card rounded-3xl shadow-2xl border border-border overflow-hidden mx-auto max-w-md">
              <div className="p-6 space-y-4">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-foreground mb-1">Quick Actions</h3>
                  <p className="text-sm text-muted-foreground">Choose an action to get started</p>
                </div>
                
                <div className="space-y-3">
                  {quickActions.map((action, index) => (
                    <motion.button
                      key={action.label}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={action.action}
                      className="w-full flex items-center p-4 rounded-2xl bg-muted hover:bg-muted/80 transition-colors group"
                    >
                      <div className="bg-primary text-white p-3 rounded-xl mr-4 group-hover:bg-primary-hover transition-colors">
                        {action.icon}
                      </div>
                      <div className="flex-1 text-left">
                        <h4 className="font-medium text-foreground">{action.label}</h4>
                        <p className="text-sm text-muted-foreground">{action.description}</p>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {(showQuickActions || showScanModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setShowQuickActions(false);
              setShowScanModal(false);
            }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm -z-10"
          />
        )}
      </AnimatePresence>

      {/* Navigation Bar */}
      <div 
        data-mobile-nav 
        className={cn(
          "thick-glass-effect rounded-full py-2 px-0.5 mx-auto max-w-md relative",
          user.floatingBarTheme === 'dark' ? 'floating-bar-dark' : 'floating-bar-light'
        )}
      >
        <div className="flex justify-around items-center h-full">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex flex-col items-center justify-center transition-colors rounded-full p-1 focus:outline-none focus-visible:outline-none active:outline-none",
                item.special ? "relative text-white" : "",
                item.special ? "" : user.floatingBarTheme === 'dark' ? "text-white hover:text-white" : "text-black hover:text-black"
              )}
              onClick={handleButtonClick(item.path, !!item.special, item.special ? handlePlusClick : undefined)}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {item.special ? (
                <motion.div 
                  className={cn(
                    "text-white p-2 rounded-full transition-colors",
                    showQuickActions ? "bg-red-500 hover:bg-red-600" : "bg-primary hover:bg-primary-hover"
                  )}
                  animate={{ rotate: showQuickActions ? 180 : 0 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                >
                  {item.icon}
                </motion.div>
              ) : (
                <div className="relative flex items-center justify-center">
                  {item.active && (
                    <motion.div 
                      className="absolute inset-0 rounded-full -m-4 bg-cover bg-center bg-no-repeat"
                      style={{
                        backgroundImage: 'url(/plate-button.png)',
                        backgroundSize: 'contain'
                      }}
                      animate={clickedButton === item.path ? {
                        scale: [1, 1.3, 1],
                        rotate: [0, 5, -5, 0]
                      } : {}}
                      transition={{
                        duration: 0.2,
                        ease: "easeOut"
                      }}
                    />
                  )}
                  <div className="w-6 h-6 flex items-center justify-center relative z-10">
                    {item.icon}
                  </div>
                </div>
              )}
            </Link>
          ))}
        </div>
        
        {/* Hidden file inputs for different capture methods */}
        {/* Inventory Camera Input */}
        <input
          ref={inventoryCameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => handleFileChange(e, 'inventory')}
          style={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '1px',
            height: '1px',
            opacity: 0,
            pointerEvents: 'none',
            zIndex: -1
          }}
        />
        {/* Inventory File Input */}
        <input
          ref={inventoryFileRef}
          type="file"
          accept="image/*"
          onChange={(e) => handleFileChange(e, 'inventory')}
          style={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '1px',
            height: '1px',
            opacity: 0,
            pointerEvents: 'none',
            zIndex: -1
          }}
        />
        {/* Meal Camera Input */}
        <input
          ref={mealCameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => handleFileChange(e, 'meal')}
          style={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '1px',
            height: '1px',
            opacity: 0,
            pointerEvents: 'none',
            zIndex: -1
          }}
        />
        {/* Meal File Input */}
        <input
          ref={mealFileRef}
          type="file"
          accept="image/*"
          onChange={(e) => handleFileChange(e, 'meal')}
          style={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '1px',
            height: '1px',
            opacity: 0,
            pointerEvents: 'none',
            zIndex: -1
          }}
        />
      </div>
    </motion.div>
  );
};

export default MobileNav;